import EventStore from '@/store/events'
import UserStore from '@/store/user'
import GroupStore from '@/store/groups'
import PostsStore from '@/store/posts'
import ConversationMessageStore from '@/store/conversations-messages'
import { User, Event, Post, Group, ConversationMessage, Conversation } from '@/classes'
import { useDataFetch } from '@/utils/useDataFetch'
import { unblockUserAPI } from '@/api/user'
import { sentimentCreateAPI } from '@/api/sentiments'

const { removePost } = useDataFetch()
interface ContextParams {
  event?: Event
  post?: Post
  postParent?: Post
  user?: User
  message?: ConversationMessage & {
    isLoading?: boolean
    isFailed?: boolean
  }
  conversation?: Conversation
  group?: Group
  currentUser?: User
}

export interface ContextItem2 {
  id: string
  label: string
  to?: RouteLocationRaw
  type?: 'base' | 'important'
  isDisabled?: boolean
  isChecked?: boolean
  isSelected?: boolean
  isGreyed?: boolean
  emit?: 'react-list' | 'answer'
  action?: () => void
  children?: ContextItem2[]
}

export const useEventActions = (params: ContextParams): ContextItem2[] => {  
  if (!params.event) return []

  const route = useRoute()
  const router = useRouter()

  return [
    {
      id: 'id',
      label: useNuxtApp().$i18n.t('components.Event.context.id'),
      action: async () => {
        if (params.event) copyToClipboard(params.event._id)
      },
      isDisabled: !params.currentUser?.isEditor
    },
    {
      id: 'share',
      label: useNuxtApp().$i18n.t('components.Event.context.share'),
      action: async () => {
        if (params.event) router.push({ query: { ...(route?.query ?? {}), share: 'true', shareEvent: params.event._id } })
      },
      isDisabled: params.event.status !== 'published'
    },
    {
      id: 'update',
      label: useNuxtApp().$i18n.t('components.Event.context.update'),
      to: { name: 'app-events-slug-edit', params: { slug: params.event?.slug } },
      isDisabled: !params.event?.isOrganizing(params.currentUser?._id) || params.event?.isPastAfter
    },
    {
      id: 'update-status',
      label: useNuxtApp().$i18n.t('components.Event.context.cancel'),
      to: { name: 'app-events-slug', params: { slug: params.event?.slug } },
      isDisabled: !params.event?.isAttending(params.currentUser?._id) || params.event?.isPastAfter || params.event?.isOrganizing(params.currentUser?._id)
    },
    {
      id: 'report',
      label: useNuxtApp().$i18n.t('components.Event.context.report'),
      to: { query: { ...route.query, report: params.event._id, reportType: 'event' } },
      type: 'important',
      isDisabled: params.currentUser?._id === params.event?.owner,
    }
  ].filter(i => !i.isDisabled) as ContextItem2[]
}

export const usePostActions = (params: ContextParams): ContextItem2[] => {  
  if (!params.post) return []

  const route = useRoute()
  const router = useRouter()

  const userStore = UserStore()
  const postsStore = PostsStore()
  const { currentUser } = storeToRefs(userStore)

  return [
    {
      id: 'pin',
      label: useNuxtApp().$i18n.t('components.Post.context.pin'),
      action: async () => {
        if (!params.post) return

       await postsStore.pin(params.post._id, true)
      },
      isDisabled: params.post?.isPinned || !params.group?.hasOrgaRights(params.currentUser?._id),
    },
    {
      id: 'pin',
      label: useNuxtApp().$i18n.t('components.Post.context.unpin'),
      action: async () => {
        if (!params.post) return

       await postsStore.pin(params.post._id, false)
      },
      isDisabled: !params.post?.isPinned || !params.group?.hasOrgaRights(params.currentUser?._id),
    },
    {
      id: 'edit',
      label: useNuxtApp().$i18n.t('components.Post.context.edit'),
      isDisabled: params.post.owner !== params.currentUser?._id,
      to: { query: { ...route.query, postEdit: params.post._id } },
    },
    {
      id: 'reaction-list',
      label: useNuxtApp().$i18n.t('components.Context.reactions'),
      emit: 'react-list',
      isDisabled: !params.post?.reactions || params.post.reactions.length <= 0
    },
    // {
    //   id: 'cheers',
    //   label: useNuxtApp().$i18n.t('components.Cheers.context.action'),
    //   emit: 'cheers',
    //   isDisabled: params.post.owner === currentUser.value?._id || params.post.cheers.find(c => c.owner === currentUser.value?._id)
    // },
    {
      id: 'post-dislike',
      label: useNuxtApp().$i18n.t('components.Post.context.hide'),
      isDisabled: params.post?.owner === params.currentUser?._id || (currentUser.value?.groups ?? []).includes(params.post?.group ?? ''),
      children: [
        {
          id: 'post-dislike',
          label: useNuxtApp().$i18n.t('components.Post.context.hidePost'),
          action: async () => {
            if (!params.post) return

            postsStore.deleteItem(params.post._id)

            sentimentCreateAPI('blocked', {
              postIds: [ params.post._id ]
            })
          }
        },
        {
          id: 'post-dislike-user',
          label: useNuxtApp().$i18n.t('components.Post.context.hideUser'),
          isDisabled: !params.post?.owner,
          action: async () => {
            if (!params.post) return

            postsStore.deleteItem(params.post._id)

            sentimentCreateAPI('blocked', {
              userIds: [ params.post.owner ]
            })
          },
        },
        {
          id: 'post-dislike-group',
          label: useNuxtApp().$i18n.t('components.Post.context.hideGroup'),
          isDisabled: !params.post?.group,
          action: async () => {
            if (!params.post?.group) return

            postsStore.deleteItem(params.post._id)

            userStore.edit({
              hiddenGroups: [ ...new Set([
                ...(currentUser.value?.hiddenGroups ?? []),
                params.post.group
              ]) ]
            }, {
              silent: true
            })
          }
        }
      ].filter(p => !p.isDisabled)
    },
    {
      id: 'post-mute',
      label: useNuxtApp().$i18n.t('components.Context.mute'),
      children: [
        {
          id: 'post-mute-hour',
          label: useNuxtApp().$i18n.t('components.Context.muteHours'),
          action: async () => {
            if (!params.post) return
            await userStore.muteChannel(params.post._id, 'post', 'hours')
          }
        },
        {
          id: 'post-mute-week',
          label: useNuxtApp().$i18n.t('components.Context.muteWeek'),
          action: async () => {
            if (!params.post) return
            await userStore.muteChannel(params.post._id, 'post', 'week')
          }
        },
        {
          id: 'post-mute-always',
          label: useNuxtApp().$i18n.t('components.Context.muteInfinite'),
          action: async () => {
            if (!params.post) return
            await userStore.muteChannel(params.post._id, 'post', 'indefinite')
          }
        }
      ],
      isDisabled: currentUser.value?.isMuted(params.post._id, 'post')
    },
    {
      id: 'conversation-unmute',
      label: useNuxtApp().$i18n.t('components.Context.unmute'),
      action: async () => {
        if (!params.post) return
        await userStore.muteChannel(params.post._id, 'post', 'unmute')
      },
      isDisabled: !currentUser.value?.isMuted(params.post._id, 'post')
    },
    {
      id: 'copy-contenu',
      label: useNuxtApp().$i18n.t('components.Context.copy'),
      action: async () => {
        if (params.post) copyToClipboard(stripTags(params.post.content))
      }
    },
    {
      id: 'copy',
      label: useNuxtApp().$i18n.t('components.Context.share'),
      action: async () => {
        if (params.post) {
          copyToClipboard(useRuntimeConfig().public.APP_URL + '?post=' + (params.post._id))
        }
      }
    },
    // {
    //   id: 'event',
    //   label: useNuxtApp().$i18n.t('components.Post.context.event'),
    //   action: async () => {
    //     if (params.post && params.event) {
    //       router.push({ name: 'app-events-slug', params: { slug: params.event.slug }})
    //     }
    //   },
    //   isDisabled: !params.event
    // },
    // {
    //   id: 'group',
    //   label: useNuxtApp().$i18n.t('components.Post.context.group'),
    //   action: async () => {
    //     if (params.post && params.group) {
    //       router.push({ query: { ...route.query, group: params.group._id }})
    //     }
    //   },
    //   isDisabled: !params.group
    // },
    {
      id: 'post-delete',
      isAutoClose: true,
      isDisabled: !params.currentUser?.isEditor && params.post?.owner !== params.currentUser?._id && params.postParent?.owner !== params.currentUser?._id,
      type: 'important',
      label: params.post?.parent ? useNuxtApp().$i18n.t('components.Post.context.deleteComment') : useNuxtApp().$i18n.t('components.Post.context.delete'),
      action: async () => {
        if (!params.post) return

        const postsStore = PostsStore()

        if (params.post._id) await removePost(params.post._id)
        
        await postsStore.deleteItem(params.post._id)
        await postsStore.fetchOne(params.post?.parent ?? params.post._id)

        return true
      }
    },
    {
      id: 'post-report',
      label: useNuxtApp().$i18n.t('components.Post.context.report'),
      to: { query: { ...route.query, report: params.post?._id, reportType: 'post' } },
      type: 'important',
      isDisabled: params.currentUser?._id === params.post?.owner,
    }
  ].filter(i => !i.isDisabled) as ContextItem2[]
}

export const useUserGroupActions = (params: ContextParams): ContextItem2[] => {  
  if (!params.group || !params.user) return []

  const groupStore = GroupStore()

  return [
    {
      id: 'user-role',
      label: useNuxtApp().$i18n.t('components.Group.context.role'),
      isDisabled: !params.group.hasAdminRights(params.currentUser?._id),
      children: [
        {
          id: 'user-role-admin',
          label: useNuxtApp().$i18n.t('components.Group.roles.admin', 1),
          isGreyed: params.group.hasAdminRights(params.currentUser?._id) && params.user._id === params.currentUser?._id,
          isSelected: params.group.isAdmin(params.user?._id),
          action: async () => {
            if (!params.user || !params.group) return
            
            await groupStore.rolesSet(params.user._id, 'admin', params.group?._id)
          }
        },
        {
          id: 'user-role-member',
          label: useNuxtApp().$i18n.t('components.Group.roles.member', 1),
          isGreyed: params.group.hasAdminRights(params.currentUser?._id) && params.user._id === params.currentUser?._id,
          isSelected: !params.group.isAdmin(params.user?._id) && !params.group.isOrga(params.user?._id),
          action: async () => {
            if (!params.user || !params.group) return
            
            await groupStore.rolesSet(params.user._id, 'member', params.group?._id)
          }
        }
      ]
    },
    {
      id: 'group-remove',
      label: useNuxtApp().$i18n.t('components.Group.context.remove'),
      type: 'important',
      isDisabled: !params.group.hasAdminRights(params.currentUser?._id) || params.currentUser?._id === params.user._id,
      action: async () => {
        if (!params.group || !params.user) return false
       
        groupStore.kick(params.user._id, params.group._id)

        return true
      }
    }
  ].filter(i => !i.isDisabled) as ContextItem2[]
}

export const useUserEventActions = (params: ContextParams): ContextItem2[] => {
  if (!params.event || !params.user) return []

  const eventStore = EventStore()
  const route = useRoute()

  return [
    {
      id: 'roles',
      label: useNuxtApp().$i18n.t('components.Event.context.role'),
      isDisabled: (!params.event.isEventAdmin(params.currentUser) && !params.group?.hasOrgaRights(params.currentUser?._id)),
      children: [
        {
          id: 'user-event-orga',
          label: useNuxtApp().$i18n.t('components.Event.attendeeState.organizer'),
          isSelected: params.event.isOrganizing(params.user?._id),
          action: async () => {
            if (!params.user || !params.event) return
            await eventStore.roleSet(params.user._id, params.event._id, 'organizer')
          }
        },
        {
          id: 'user-event-attendee',
          label: useNuxtApp().$i18n.t('components.Event.attendeeState.attending'),
          isSelected: !params.event.isOrganizing(params.user?._id),
          isGreyed: params.event.organizers.length === 1 && params.user._id === params.currentUser?._id,
          action: async () => {
            if (!params.user || !params.event) return
            await eventStore.roleSet(params.user._id, params.event._id, 'attendee')
          }
        }
      ]
    },
    {
      id: 'kick',
      label: useNuxtApp().$i18n.t('components.Event.context.kick'),
      type: 'important',
      to: { query: { ...route.query, kick: params.user?._id } },
      isDisabled: (!params.event.isEventAdmin(params.currentUser) && !params.group?.hasOrgaRights(params.currentUser?._id)) || params.user?._id === params.currentUser?._id,
    }
  ].filter(i => !i.isDisabled) as ContextItem2[]
}

export const useUserRelationshipActions = (params: ContextParams): ContextItem2[] => {  
  if (!params.user) return []

  const userStore = UserStore()

  return [

  ] as ContextItem2[]
}

export const useUserActions = (params: ContextParams): ContextItem2[] => {  
  if (!params.user) return []

  const route = useRoute()
  const userStore = UserStore()

  return [
    {
      id: 'user-profile',
      label: useNuxtApp().$i18n.t('components.User.context.profile', { name: params.user?.name }),
      to: { query: { ...route.query, user: params.user?.username } }
    },
    {
      id: 'user-block',
      type: 'important',
      label: useNuxtApp().$i18n.t('components.User.context.block', { name: params.user?.name }),
      to: { query: { ...route.query, block: params.user?._id } },
      isDisabled: params.currentUser?._id === params.user?._id || (params.currentUser?.blocked ?? []).includes(params.user?._id ?? '') || params.user.type === 'bot',
    },
    {
      id: 'user-unblock',
      label: useNuxtApp().$i18n.t('components.User.context.unblock', { name: params.user?.name }),
      action: async () => {
        if (!params.user) return

        await unblockUserAPI(params.user._id)
        await userStore.initUser(true)
        
        return
      },
      isDisabled: !(params.currentUser?.blocked ?? []).includes(params.user?._id ?? ''),
    },
    {
      id: 'user-report',
      label: useNuxtApp().$i18n.t('components.User.context.report', { name: params.user?.name }),
      to: { query: { ...route.query, report: params.user?._id, reportType: 'user' } },
      type: 'important',
      isDisabled: params.currentUser?._id === params.user?._id || params.user.type === 'bot',
    }
  ].filter(i => !i.isDisabled) as ContextItem2[]
}

export const useReact = (params: ContextParams) => {

  return []
}

export const useConversation = (params: ContextParams) => {
  if (!params.currentUser || !params.conversation) return []

  const userStore = UserStore()
  const { currentUser } = storeToRefs(userStore)

  return [
    {
      id: 'conversation-mute',
      label: useNuxtApp().$i18n.t('components.Context.mute'),
      children: [
        {
          id: 'conversation-mute-hour',
          label: useNuxtApp().$i18n.t('components.Context.muteHours'),
          action: async () => {
            if (!params.conversation) return
            await userStore.muteChannel(params.conversation?._id, 'conversation', 'hours')
          }
        },
        {
          id: 'conversation-mute-week',
          label: useNuxtApp().$i18n.t('components.Context.muteWeek'),
          action: async () => {
            if (!params.conversation) return
            await userStore.muteChannel(params.conversation?._id, 'conversation', 'week')
          }
        },
        {
          id: 'conversation-mute-always',
          label: useNuxtApp().$i18n.t('components.Context.muteInfinite'),
          action: async () => {
            if (!params.conversation) return
            await userStore.muteChannel(params.conversation?._id, 'conversation', 'indefinite')
          }
        }
      ],
      isDisabled: currentUser.value?.isMuted(params.conversation._id)
    },
    {
      id: 'conversation-unmute',
      label: useNuxtApp().$i18n.t('components.Context.unmute'),
      action: async () => {
        if (!params.conversation) return
        await userStore.muteChannel(params.conversation?._id, 'conversation', 'unmute')
      },
      isDisabled: !currentUser.value?.isMuted(params.conversation._id, 'conversation')
    }
  ].filter(i => !i.isDisabled) as ContextItem2[]
}

export const useMessage = (params: ContextParams) => {
  if (!params.message) return []

  const conversationMessageStore = ConversationMessageStore()

  return [
    {
      id: 'answer',
      label: useNuxtApp().$i18n.t('components.Message.context.reply'),
      isDisabled: params.message.isLoading || params.message.isFailed,
      emit: 'answer'
    },
    {
      id: 'edit',
      label: useNuxtApp().$i18n.t('components.Message.context.edit'),
      isDisabled: params.message.isLoading || params.message.isFailed || params.message.owner !== params.currentUser?._id,
      emit: 'edit'
    },
    {
      id: 'reaction-list',
      label: useNuxtApp().$i18n.t('components.Context.reactions'),
      emit: 'react-list',
      isDisabled: !params.message?.reactions || params.message.reactions.length <= 0 || params.conversation?.isAnonymous
    },
    {
      id: 'copy-message',
      label: useNuxtApp().$i18n.t('components.Message.context.copy'),
      action: async () => {
        if (params.message) copyToClipboard(stripTags(params.message.content))
      }
    },
    {
      id: 'retry-message',
      label: useNuxtApp().$i18n.t('components.Message.context.retry'),
      isDisabled: !params.message?.isFailed,
      emit: 'retry',
      type: 'important',
    },
    {
      id: 'delete',
      label: useNuxtApp().$i18n.t('components.Message.context.delete'),
      emit: 'delete',
      isAutoClose: true,
      isDisabled: (!params.currentUser?.isEditor && params.message?.owner !== params.currentUser?._id),
      type: 'important',
      action: async () => {
        if (!params.message) return

        await conversationMessageStore.remove(params.message._id)

        return true
      }
    },
  ].filter(i => !i.isDisabled) as ContextItem2[]

}

export const useGroup = (params: ContextParams) => {
  if (!params.group) return []

  const route = useRoute()
  const router = useRouter()

  const groupStore = GroupStore()

  return [
    {
      id: 'share',
      label: useNuxtApp().$i18n.t('components.Context.share'),
      action: async () => {
        if (params.group) router.push({ query: { ...(route?.query ?? {}), share: 'true', shareGroup: params.group._id } })
      },
      isDisabled: !params.group.isMember(params.currentUser?._id) || (params.group.application === 'invite' && !params.group.hasOrgaRights(params.currentUser?._id))
    },
    {
      id: 'group-report',
      label: useNuxtApp().$i18n.t('components.Group.context.report'),
      type: 'important',
      to: { query: { ...route.query, report: params.group._id, reportType: 'group' } }
    },
    {
      id: 'group-leave',
      label: useNuxtApp().$i18n.t('components.Group.context.leave'),
      type: 'important',
      isDisabled: !params.group.isMember(params.currentUser?._id),
      action: async () => {
        if (!params.group) return false

        groupStore.leave(params.group._id)

        return true
      }
    }
  ].filter(i => !i.isDisabled) as ContextItem2[]
}

export const ContextActions = {
  react: useReact,
  message: useMessage,
  event: useEventActions,
  post: usePostActions,
  group: useGroup,
  conversation: useConversation,
  'user-group': useUserGroupActions,
  'user-event': useUserEventActions,
  'user-relationship': useUserRelationshipActions,
  user: useUserActions
}